import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { api } from '../api/client'
import type { MedScheda, MedVoce } from '../api/types'
import { Card, EmptyState, ErrorBanner, PageSection, SectionedPage, Spinner, formatDateTime, formatDay } from '../components/common'
import { DataTable } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { useAuth } from '../auth/AuthContext'
import { livelloModulo } from '../auth/permessi'
import { BadgeStatoMed, fonteModello } from '../components/med/comuni'
import { FinestraEsclusione, FinestraScheda } from '../components/med/Finestre'
import { CollegamentiDispositivo, DoveDispositivo, UsoDispositivo, useContesto } from '../components/med/Contesto'
import { MappaClinica } from '../components/med/MappaClinica'
import { CespiteContratti } from '../components/med/Cespite'
import { FotoDocumenti } from '../components/med/Foto'
import { BadgeStatoRichiamo, etichettaAutorita, nomePaese } from '../components/med/Richiami'
import { VerificheDispositivo } from '../components/med/Verifiche'

/**
 * The page of a medical device in CENSO MED (ADR-0027): the clinical
 * engineering record, what CENSO saw of the device on the network, the
 * catalogue model it belongs to and the security advisories on it.
 */

function voce(v: MedVoce | null): string {
  return v ? `${v.name}${v.code ? ` (${v.code})` : ''}` : '—'
}

function siNo(v: boolean | null): string {
  return v === null ? '—' : v ? t('sì') : t('no')
}

/** The labels of the CI facts the page shows (keys from `med_device_service`). */
function etichettaFatto(chiave: string): string {
  const etichette: Record<string, string> = {
    hostname: t('Nome host'),
    fqdn: t('FQDN'),
    mgmt_ip: t('Indirizzo IP'),
    mac: t('MAC'),
    vlan: t('VLAN'),
    device_type: t('Tipo di dispositivo'),
    medical_category: t('Categoria'),
    os: t('Sistema operativo'),
    os_version: t('Versione del sistema operativo'),
    firmware: t('Firmware'),
    firmware_version: t('Versione firmware'),
    serial: t('Numero di serie'),
    discovered_via: t('Rilevato con'),
    seen_by: t('Visto da'),
    recognition: t('Riconosciuto da'),
  }
  return etichette[chiave] ?? chiave
}

function Scheda({ d }: { d: MedScheda }) {
  return (
    <>
      <Card title={t('Identificazione')}>
        <dl className="kv">
          <dt>{t('Produttore')}</dt><dd>{d.vendor || '—'}</dd>
          <dt>{t('Serie')}</dt><dd>{d.series || '—'}</dd>
          <dt>{t('Modello')}</dt>
          <dd>
            {d.model || <span className="text-muted">{d.series ? t('da determinare') : '—'}</span>}
            {d.model && d.model_source && <small className="text-muted"> · {fonteModello(d.model_source)}</small>}
            {!d.model && d.series && (
              <div className="field__hint">
                {t('CENSO riconosce la serie ma non ancora il modello. Lo prende dall’inventario di riferimento dell’ospedale (stesso MAC), dal nome con cui il dispositivo si presenta, o da chi lo scrive nella scheda.')}
              </div>
            )}
          </dd>
          <dt>{t('Categoria')}</dt><dd>{d.category || '—'}</dd>
          <dt>{t('N. inventario')}</dt><dd className="mono">{d.inventory_number ?? '—'}</dd>
          <dt>{t('Numero di serie')}</dt><dd className="mono">{d.serial_number ?? '—'}</dd>
          <dt>{t('UDI-DI')}</dt><dd className="mono">{d.udi_di ?? '—'}</dd>
          <dt>{t('Codice CND')}</dt><dd><span className="mono">{d.cnd_code ?? '—'}</span>{d.cnd ? ` · ${d.cnd.description}` : ''}</dd>
          <dt>{t('Codice CIVAB')}</dt><dd className="mono">{d.civab_code ?? '—'}</dd>
        </dl>
      </Card>
      <Card title={t('Dove si trova')}>
        <dl className="kv">
          <dt>{t('Sede')}</dt><dd>{voce(d.location)}</dd>
          <dt>{t('Reparto')}</dt><dd>{voce(d.org_unit)}</dd>
          <dt>{t('Stanza')}</dt><dd>{d.room ?? '—'}</dd>
          <dt>{t('Letto')}</dt><dd>{d.bed ?? '—'}</dd>
          <dt>{t('Centro di costo')}</dt><dd>{voce(d.cost_center)}</dd>
        </dl>
      </Card>
      <Card title={t('Stato e responsabile')}>
        <dl className="kv">
          <dt>{t('Stato')}</dt><dd><BadgeStatoMed stato={d.status} />{d.status_reason ? ` ${d.status_reason}` : ''}</dd>
          <dt>{t('Responsabile')}</dt><dd>{d.responsible_name ?? '—'}{d.responsible_email ? ` · ${d.responsible_email}` : ''}</dd>
          <dt>{t('Collaudo')}</dt><dd>{formatDay(d.acceptance_date)}</dd>
          <dt>{t('Installazione')}</dt><dd>{formatDay(d.install_date)}</dd>
        </dl>
      </Card>
      <Card title={t('Sicurezza elettrica (CEI EN 62353)')}>
        <dl className="kv">
          <dt>{t('Classe di protezione')}</dt><dd>{d.electrical_class ?? '—'}</dd>
          <dt>{t('Parte applicata')}</dt><dd>{d.applied_part ?? '—'}</dd>
          <dt>{t('Collegato al paziente')}</dt><dd>{siNo(d.patient_connected)}</dd>
        </dl>
      </Card>
      {d.notes && (
        <Card title={t('Note')}>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{d.notes}</p>
        </Card>
      )}
      <p className="field__hint">
        {d.edited_at
          ? t('Ultima modifica il {quando} da {chi}.', { quando: formatDateTime(d.edited_at), chi: d.edited_by ?? '—' })
          : t('Scheda creata da CENSO il {quando}: nessuno l’ha ancora compilata.', { quando: formatDateTime(d.created_at) })}
      </p>
    </>
  )
}

function Rilevazione({ d }: { d: MedScheda }) {
  if (!d.ci) {
    return <EmptyState title={t('Il dispositivo non è più nella CMDB.')} hint={t('La scheda resta per la sua storia.')} />
  }
  return (
    <Card title={t('Cosa vede CENSO')}>
      <dl className="kv kv--attributi">
        <dt>{t('Nome')}</dt><dd>{d.ci.name ?? '—'}</dd>
        <dt>{t('Classe')}</dt><dd>{d.ci.ci_class ? t(d.ci.ci_class) : '—'}{d.ci.class_confidence ? ` · ${t(d.ci.class_confidence)}` : ''}</dd>
        {d.ci.class_reason && (<><dt>{t('Perché')}</dt><dd>{d.ci.class_reason}</dd></>)}
        {d.ci.facts.map((f) => (
          <div key={f.key} style={{ display: 'contents' }}>
            <dt>{etichettaFatto(f.key)}</dt>
            <dd className={['mgmt_ip', 'mac', 'fqdn'].includes(f.key) ? 'mono' : undefined}>{f.value}</dd>
          </div>
        ))}
        <dt>{t('Prima volta visto')}</dt><dd>{formatDateTime(d.ci.first_seen)}</dd>
        <dt>{t('Ultimo contatto')}</dt><dd>{formatDateTime(d.ci.last_seen)}</dd>
      </dl>
    </Card>
  )
}

function Registro({ d, scrive, onCambiato }: { d: MedScheda; scrive: boolean; onCambiato: () => void }) {
  const r = d.registry
  const [errore, setErrore] = useState<unknown>(null)
  if (!d.public_sources.includes('it')) return null
  if (!r) {
    return (
      <Card title={t('Registro del Ministero della Salute')}>
        <p className="field__hint" style={{ margin: 0 }}>
          {d.model
            ? t('Nessuna corrispondenza nel registro dei dispositivi medici per questo modello.')
            : t('Si conosce solo la serie: il registro si consulta per un modello preciso.')}
        </p>
      </Card>
    )
  }
  async function usaCnd() {
    setErrore(null)
    try {
      await api.medDeviceUpdate(d.id, { cnd_code: r!.cnd_code, ...(r!.civab ? { civab_code: r!.civab } : {}) })
      onCambiato()
    } catch (e) {
      setErrore(e)
    }
  }
  return (
    <Card title={t('Registro del Ministero della Salute')} actions={
      scrive && r.cnd_code && r.cnd_code !== d.cnd_code
        ? <button type="button" className="secondary" onClick={() => void usaCnd()}>{t('Usa questo codice CND')}</button> : undefined}>
      <ErrorBanner error={errore} />
      <p className="field__hint" style={{ marginTop: 0 }}>
        {r.verified ? t('Corrispondenza verificata dall’amministrazione della piattaforma.')
          : r.confidence === 'high' ? t('Corrispondenza probabile: il codice del modello compare nel registro una volta sola.')
            : t('Corrispondenza probabile, da controllare: più voci del registro portano il codice del modello.')}
      </p>
      <dl className="kv">
        <dt>{t('Denominazione commerciale')}</dt><dd>{r.name}</dd>
        <dt>{t('Fabbricante')}</dt><dd>{r.manufacturer}</dd>
        <dt>{t('Codice catalogo')}</dt><dd className="mono">{r.catalogue_code || '—'}</dd>
        <dt>{t('Classificazione CND')}</dt><dd><span className="mono">{r.cnd_code}</span>{r.cnd_description ? ` · ${r.cnd_description}` : ''}</dd>
        <dt>{t('Codice CIVAB')}</dt><dd className="mono">{r.civab ?? '—'}</dd>
        <dt>{t('Iscritto al Repertorio')}</dt><dd>{r.repertorio ? t('sì') : t('no')}</dd>
        <dt>{t('Fuori commercio dal')}</dt><dd>{r.end_of_sale ? formatDay(r.end_of_sale) : t('in commercio')}</dd>
        <dt>{t('Numero nel registro')}</dt><dd className="mono">{r.progressive}</dd>
      </dl>
    </Card>
  )
}

/** What the FDA's GUDID says about the model (openFDA device/udi). */
function SchedaFda({ d }: { d: MedScheda }) {
  const f = d.fda
  if (!d.public_sources.includes('fda')) return null
  if (!f) {
    return (
      <Card title={t('FDA — identificazione del dispositivo (GUDID)')}>
        <p className="field__hint" style={{ margin: 0 }}>
          {d.model
            ? t('Nessuna corrispondenza nella banca dati GUDID della FDA per questo modello (o non ancora cercata).')
            : t('Si conosce solo la serie: la banca dati si consulta per un modello preciso.')}
        </p>
      </Card>
    )
  }
  const gmdn = f.gmdn[0]
  return (
    <Card title={t('FDA — identificazione del dispositivo (GUDID)')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {f.verified ? t('Corrispondenza verificata dall’amministrazione della piattaforma.')
          : f.confidence === 'high' ? t('Corrispondenza probabile: un solo modello della FDA porta il codice del modello.')
            : t('Corrispondenza probabile, da controllare: più modelli della FDA portano il codice del modello.')}
      </p>
      <dl className="kv">
        <dt>{t('Nome commerciale')}</dt><dd>{f.brand_name || '—'}</dd>
        <dt>{t('Modello')}</dt><dd>{f.model_number || '—'}</dd>
        <dt>{t('Codice catalogo')}</dt><dd className="mono">{f.catalogue_code || '—'}</dd>
        <dt>{t('Fabbricante')}</dt><dd>{f.manufacturer || '—'}</dd>
        <dt>{t('Destinazione d’uso')}</dt>
        <dd>
          {gmdn ? <>{gmdn.definition || gmdn.name}<br /><small className="muted">{t('Definizione del termine GMDN {codice} «{nome}»: la banca dati FDA non riporta la destinazione d’uso del fabbricante.', { codice: gmdn.code, nome: gmdn.name })}</small></> : '—'}
        </dd>
        <dt>{t('Classe FDA')}</dt><dd>{f.device_class || '—'}</dd>
        <dt>{t('Codice prodotto FDA')}</dt><dd><span className="mono">{f.product_code || '—'}</span>{f.device_name ? ` · ${f.device_name}` : ''}</dd>
        <dt>{t('Regolamento')}</dt><dd className="mono">{f.regulation_number || '—'}</dd>
        <dt>{t('Specialità')}</dt><dd>{f.medical_specialty || '—'}</dd>
        <dt>{t('Sicurezza in risonanza magnetica')}</dt><dd>{f.mri_safety || '—'}</dd>
        <dt>{t('UDI-DI primario')}</dt><dd className="mono">{f.primary_di || '—'}</dd>
      </dl>
    </Card>
  )
}

/** EUDAMED's status of a device on the market, in words. */
function statoMercatoUe(s: string): string {
  const e: Record<string, string> = {
    'on-the-market': t('sul mercato'),
    'no-longer-placed-on-the-market': t('non più immesso sul mercato'),
    'not-intended-for-eu-market': t('non destinato al mercato UE'),
  }
  return e[s] ?? s
}

/** What EUDAMED says about the model (the European database). */
function SchedaEudamed({ d, scrive, onCambiato }: { d: MedScheda; scrive: boolean; onCambiato: () => void }) {
  const e = d.eudamed
  const [errore, setErrore] = useState<unknown>(null)
  if (!d.public_sources.includes('eu')) return null
  if (!e) {
    return (
      <Card title={t('EUDAMED — banca dati europea')}>
        <p className="field__hint" style={{ margin: 0 }}>
          {d.model
            ? t('Nessuna corrispondenza in EUDAMED per questo modello (o non ancora cercata).')
            : t('Si conosce solo la serie: la banca dati si consulta per un modello preciso.')}
        </p>
      </Card>
    )
  }
  async function usaEmdn() {
    setErrore(null)
    try {
      await api.medDeviceUpdate(d.id, { cnd_code: e!.emdn_code })
      onCambiato()
    } catch (x) {
      setErrore(x)
    }
  }
  return (
    <Card title={t('EUDAMED — banca dati europea')} actions={
      scrive && e.emdn_code && e.emdn_code !== d.cnd_code
        ? <button type="button" className="secondary" onClick={() => void usaEmdn()}>{t('Usa questo codice EMDN')}</button> : undefined}>
      <ErrorBanner error={errore} />
      <p className="field__hint" style={{ marginTop: 0 }}>
        {e.verified ? t('Corrispondenza verificata dall’amministrazione della piattaforma.')
          : e.confidence === 'high' ? t('Corrispondenza probabile: un solo dispositivo del fabbricante in EUDAMED porta il codice del modello.')
            : t('Corrispondenza probabile, da controllare: più dispositivi del fabbricante in EUDAMED portano il codice del modello.')}
      </p>
      <dl className="kv">
        <dt>{t('Nome commerciale')}</dt><dd>{e.name || '—'}</dd>
        <dt>{t('Riferimento del fabbricante')}</dt><dd className="mono">{e.catalogue_code || '—'}</dd>
        <dt>{t('Fabbricante')}</dt><dd>{e.manufacturer || '—'}{e.manufacturer_srn ? <span className="muted mono"> · {e.manufacturer_srn}</span> : null}</dd>
        <dt>{t('Classe di rischio (MDR)')}</dt><dd>{e.risk_class || '—'}</dd>
        <dt>{t('Nomenclatura EMDN')}</dt><dd><span className="mono">{e.emdn_code || '—'}</span>{e.emdn_description ? ` · ${e.emdn_description}` : ''}</dd>
        <dt>{t('Sul mercato')}</dt><dd>{statoMercatoUe(e.market_status) || '—'}{e.status_date ? ` (${formatDay(e.status_date)})` : ''}</dd>
        <dt>{t('Paesi UE in cui è disponibile')}</dt><dd>{e.markets.length ? e.markets.map(nomePaese).join(', ') : '—'}</dd>
        <dt>{t('UDI-DI primario')}</dt><dd className="mono">{e.primary_di || '—'}</dd>
        <dt>{t('Descrizione del fabbricante')}</dt><dd>{e.description || '—'}</dd>
      </dl>
    </Card>
  )
}

function Modello({ d }: { d: MedScheda }) {
  const m = d.catalogue
  if (!m) {
    return (
      <EmptyState
        title={t('Modello non ancora nel catalogo.')}
        hint={t('Il catalogo degli elettromedicali si costruisce dagli inventari di riferimento: questo modello non c’è ancora.')}
      />
    )
  }
  return (
    <Card title={t('Il modello nel catalogo')}>
      <dl className="kv">
        <dt>{t('Produttore')}</dt><dd>{m.vendor || '—'}</dd>
        <dt>{t('Modello')}</dt><dd>{m.model || '—'}</dd>
        <dt>{t('Famiglia')}</dt><dd>{m.family || '—'}</dd>
        <dt>{t('Tipo di dispositivo')}</dt><dd>{m.device_type || '—'}</dd>
        <dt>{t('Area clinica')}</dt><dd>{m.clinical_area || '—'}</dd>
        <dt>{t('Classe di rischio')}</dt><dd>{m.risk_class || '—'}</dd>
        <dt>{t('Collegato al paziente')}</dt><dd>{siNo(m.patient_connected)}</dd>
        <dt>{t('Fine vita')}</dt><dd>{formatDay(m.eol_date)}</dd>
        <dt>{t('Modulo MDS2')}</dt>
        <dd>{m.mds2_url ? <a href={m.mds2_url} target="_blank" rel="noreferrer noopener">{t('apri')}</a> : '—'}</dd>
      </dl>
    </Card>
  )
}

function Avvisi({ d }: { d: MedScheda }) {
  return (
    <>
      <Card title={t('Richiami sul modello')} actions={<Link to="/medical/recalls">{t('Tutti i richiami')}</Link>}>
        <DataTable
          rows={d.recall_list}
          rowKey={(r) => r.id}
          initialSort={{ key: 'posted', dir: 'desc' }}
          paginazione={d.recall_list.length > 25 ? undefined : false}
          strumenti={false}
          emptyText={d.model ? t('Nessun richiamo sul modello dalle fonti attive.') : t('Si conosce solo la serie: i richiami si abbinano al modello preciso.')}
          columns={[
            { key: 'number', header: t('Richiamo'), mono: true, value: (r) => r.number,
              render: (r) => (r.url ? <a href={r.url} target="_blank" rel="noreferrer noopener">{r.number}</a> : r.number) },
            { key: 'posted', header: t('Pubblicato'), type: 'date', value: (r) => r.posted ?? '', render: (r) => formatDay(r.posted) },
            { key: 'authority', header: t('Autorità'), value: (r) => etichettaAutorita(r.authority) },
            { key: 'classification', header: t('Classe'), value: (r) => r.classification },
            { key: 'reason', header: t('Motivo'), value: (r) => r.reason, render: (r) => <span className="med-testo-corto" title={r.reason}>{r.reason || r.product_description}</span> },
            { key: 'status', header: t('Gestione'), value: (r) => r.status, render: (r) => <BadgeStatoRichiamo stato={r.status} /> },
          ]}
        />
      </Card>
      <Card title={t('Avvisi di sicurezza informatica (CISA ICSMA)')}>
        <AvvisiIcsma d={d} />
      </Card>
    </>
  )
}

function AvvisiIcsma({ d }: { d: MedScheda }) {
  return (
    <DataTable
      rows={d.advisory_list}
      rowKey={(a) => a.id}
      initialSort={{ key: 'first_seen', dir: 'desc' }}
      conteggio={{ nome: t('avvisi') }}
      emptyText={t('Nessun avviso di sicurezza su questo dispositivo.')}
      columns={[
        { key: 'severity', header: t('Gravità'), filter: 'select', value: (a) => t(a.severity) },
        { key: 'title', header: t('Avviso'), value: (a) => a.title,
          render: (a) => (a.url ? <a href={a.url} target="_blank" rel="noreferrer noopener">{a.title}</a> : a.title) },
        { key: 'rule', header: t('Codice'), mono: true, value: (a) => a.rule.replace('medical-advisory:', '') },
        { key: 'accepted', header: t('Stato'), value: (a) => (a.accepted ? t('accettato') : t('aperto')) },
        { key: 'first_seen', header: t('Dal'), type: 'date', value: (a) => a.first_seen ?? '', render: (a) => formatDateTime(a.first_seen) },
      ]}
    />
  )
}

export default function MedDevice() {
  const { id = '' } = useParams()
  const { principal } = useAuth()
  const scrive = livelloModulo(principal, 'medical') === 'write'
  const dati = useAsync(() => api.medDevice(id), [id])
  const contesto = useContesto(id)
  const [finestra, setFinestra] = useState<'scheda' | 'esclusione' | null>(null)
  const d = dati.data
  const fatto = () => {
    setFinestra(null)
    void dati.reload()
    void contesto.reload()
  }
  const c = contesto.data
  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{d?.name || t('Dispositivo')}</h1>
          {d && (
            <p className="page__subtitle">
              {[d.vendor, d.model || d.series, d.inventory_number].filter(Boolean).join(' · ') || t('Elettromedicale')}
              {' '}<BadgeStatoMed stato={d.status} />
            </p>
          )}
        </div>
        {d && scrive && (
          <div className="med-azioni">
            <button type="button" onClick={() => setFinestra('scheda')}>{t('Modifica la scheda')}</button>
            <button type="button" className="secondary" onClick={() => setFinestra('esclusione')}>
              {d.excluded ? t('Rimetti fra gli elettromedicali') : t('Non è un elettromedicale')}
            </button>
          </div>
        )}
      </header>
      {d?.excluded && (
        <p className="field__hint">
          {t('Escluso dall’area: «{motivo}».', { motivo: d.excluded_reason ?? '' })}
        </p>
      )}
      {d && !d.in_scope && (
        <p className="field__hint">{t('Il CI non è più riconosciuto come elettromedicale: la scheda resta per la sua storia.')}</p>
      )}
      {d && finestra === 'scheda' && <FinestraScheda d={d} onChiudi={() => setFinestra(null)} onFatto={fatto} />}
      {d && finestra === 'esclusione' && <FinestraEsclusione d={d} onChiudi={() => setFinestra(null)} onFatto={fatto} />}
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? <Spinner /> : !d ? null : (
        <SectionedPage
          sections={[
            { id: 'scheda', label: t('Scheda') },
            { id: 'dove', label: t('Dove') },
            { id: 'collegamenti', label: t('Collegamenti ({n})', { n: c?.peers.length ?? 0 }) },
            { id: 'utilizzo', label: t('Utilizzo') },
            { id: 'manutenzioni', label: t('Manutenzioni e verifiche') },
            { id: 'cespite', label: t('Cespite e contratti') },
            { id: 'foto', label: t('Foto e documenti') },
            { id: 'rilevazione', label: t('Rilevazione') },
            { id: 'modello', label: t('Modello') },
            { id: 'avvisi', label: t('Richiami e avvisi ({n})', { n: d.advisories + d.recall_list.filter((r) => r.status === 'new' || r.status === 'in_progress').length }) },
          ]}
        >
          <PageSection id="scheda"><Scheda d={d} /></PageSection>
          <PageSection id="dove">{c ? <DoveDispositivo deviceId={d.id} c={c} onCambiato={fatto} /> : <Spinner />}</PageSection>
          <PageSection id="collegamenti">
            <MappaClinica focus={`device:${d.id}`} altezza={420} />
            {c ? <CollegamentiDispositivo c={c} /> : <Spinner />}
          </PageSection>
          <PageSection id="utilizzo">{c ? <UsoDispositivo c={c} /> : <Spinner />}</PageSection>
          <PageSection id="manutenzioni"><VerificheDispositivo deviceId={d.id} onCambiato={fatto} /></PageSection>
          <PageSection id="cespite"><CespiteContratti deviceId={d.id} /></PageSection>
          <PageSection id="foto"><FotoDocumenti deviceId={d.id} /></PageSection>
          <PageSection id="rilevazione"><Rilevazione d={d} /></PageSection>
          <PageSection id="modello"><Modello d={d} /><Registro d={d} scrive={scrive} onCambiato={fatto} /><SchedaEudamed d={d} scrive={scrive} onCambiato={fatto} /><SchedaFda d={d} /></PageSection>
          <PageSection id="avvisi"><Avvisi d={d} /></PageSection>
        </SectionedPage>
      )}
    </>
  )
}
